import { createColumnHelper } from '@tanstack/react-table'
import { TaskOverview } from 'ClientPortal/src/pages/Orders/TaskOverview/types'
import {
    ActionsCell,
    DateCell,
    DateDifferenceCell,
    EmptyCell,
    joinDefinedStrings,
    LinkCell,
    Table,
    TextCell,
    tokens,
} from 'ComponentLibrary'
import { PlainLinkCell } from 'ComponentLibrary/src/molecules/Table/Cells/BaseCells/DisplayCells'
import { useTaskSideDrawer } from 'ComponentLibrary/src/context/TaskSideDrawerContext'
import { EditableToggleCell } from 'ComponentLibrary/src/molecules/Table/Cells/BaseCells/EditableCells/EditableToggleCell'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export type TasksTableProps = {
    tasks: TaskOverview[]
    deleteTask: ({ taskId }: { taskId: string }) => void
    canDelete?: boolean
    onEdit: (taskId: string) => void
    onSelectTaskStatus: (status: string | number, task: TaskOverview) => void
    isFiltered?: boolean
    portfolioOptions: { id: string; label: string }[]
    teamOptions: { id: string; label: string }[]
}

export type SelectCellOption<TValue> = {
    value: TValue
    label: string
}

export function TasksTable({
    tasks,
    deleteTask,
    onEdit,
    onSelectTaskStatus,
    canDelete = false,
    isFiltered,
    portfolioOptions,
    teamOptions,
}: TasksTableProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'taskOverviewPage.table' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const columnHelper = createColumnHelper<TaskOverview>()
    const { openTaskSideDrawer } = useTaskSideDrawer()
    const formatRemainingText = (days: number, maxDaysToDisplay?: number) => {
        if (days < 0) {
            return t('remainingOverdueDays', { count: Math.abs(days) })
        }

        if (days === 0) {
            return t('remainingToday')
        }

        if (maxDaysToDisplay !== undefined && days > maxDaysToDisplay) {
            return t('remainingOverMaxDays', { count: maxDaysToDisplay })
        }

        return t('remainingDays', { count: days })
    }

    const tableCopy = {
        entriesLabel: (count: number) => t('entries', { count }),
        selectedEntriesLabel: (selectedCount: number, totalCount: number) =>
            t('selectedEntries', { selectedCount, totalCount, count: totalCount }),
        pageLabel: (page: number, pageCount: number) => t('pageLabel', { page, pageCount }),
        emptyFiltered: t('emptyFiltered'),
        empty: t('empty'),
    }

    const actionsCellCopy = {
        edit: t('actions.edit'),
        delete: t('actions.delete'),
        ariaLabel: (rowIndex: number) => t('actions.ariaLabel', { rowIndex }),
    }
    const portfolioIdToLabelMap: Record<string, string> = portfolioOptions.reduce(
        (map, portfolio) => {
            map[portfolio.id] = portfolio.label
            return map
        },
        {} as Record<string, string>
    )

    const columns = [
        columnHelper.display({
            id: 'status',
            cell: (info) => {
                const task = info.row.original
                return (
                    <EditableToggleCell
                        isOn={task.status === 'done'}
                        onToggle={(status) => onSelectTaskStatus(status ? 'done' : 'open', info.row.original)}
                    />
                )
            },
        }),
        columnHelper.accessor((row) => row, {
            header: t('title'),
            id: 'title',
            cell: (info) => (
                <>
                    <PlainLinkCell
                        text={info.getValue().title}
                        onClick={() => openTaskSideDrawer(info.getValue().uuid)}
                        maxWidth="200px"
                        isStrikethrough={info.getValue().status === 'done'}
                        color={info.getValue().status === 'done' ? tokens.ColorTextSubtle : tokens.ColorTextBoldest}
                    />
                </>
            ),
        }),
        columnHelper.accessor((row) => row, {
            header: tTerminology('asset'),
            id: 'linked_asset',
            cell: (info) => {
                const linkedOrder = info.getValue().order
                const linkedAssetId = linkedOrder?.asset_uuid
                return linkedAssetId ? (
                    <LinkCell
                        text={
                            linkedOrder?.asset_title || joinDefinedStrings([linkedOrder.address?.street, linkedOrder.address?.house_number])
                        }
                        path={`/assets/${linkedAssetId}`}
                        ellipsis
                    />
                ) : (
                    <EmptyCell textAlign="start" />
                )
            },
        }),
        columnHelper.accessor((row) => row, {
            header: tTerminology('order'),
            id: 'linked_order',
            cell: (info) => {
                const linkedOrder = info.getValue().order
                const orderTitle =
                    linkedOrder.asset_title || joinDefinedStrings([linkedOrder.address?.street, linkedOrder.address?.house_number])
                return <LinkCell text={orderTitle} path={`/orders/${linkedOrder.uuid}/overview`} ellipsis />
            },
        }),
        columnHelper.accessor((row) => row, {
            header: tTerminology('portfolios'),
            id: 'portfolio_uuids',
            cell: (info) => {
                const portfolioLabels = info.row.original.portfolio_uuids
                    ?.map((portfolioUuid) => portfolioIdToLabelMap[portfolioUuid])
                    .filter((portfolioLabel) => !!portfolioLabel)
                return <TextCell text={portfolioLabels ? portfolioLabels.join(', ') : '-'} maxWidth="150px" />
            },
        }),
        ...(!isEmpty(teamOptions)
            ? [
                  columnHelper.accessor((row) => row, {
                      header: t('team'),
                      id: 'team',
                      cell: (info) => {
                          const assigneeTeam = info.row.original.assignee?.team?.name
                          return <TextCell text={assigneeTeam ? assigneeTeam : '-'} maxWidth="150px" />
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('due_date', {
            id: 'dueDate',
            header: t('deadline'),
            size: 140,
            cell: (info) => <DateCell date={info.getValue()} locale={locale} />,
        }),
        columnHelper.accessor('due_date', {
            id: 'daysUntilDueDate',
            header: t('remaining'),
            size: 110,
            cell: (info) =>
                info.row.original.status !== 'done' ? (
                    <DateDifferenceCell date={info.getValue()} maxDaysToDisplay={30} formatDifferenceText={formatRemainingText} />
                ) : (
                    <EmptyCell textAlign="start" />
                ),
        }),
        columnHelper.display({
            id: 'actions',
            size: 40,
            minSize: 40,
            enableResizing: false,
            cell: (info) => {
                const task = info.row.original
                return (
                    <ActionsCell
                        rowIndex={info.row.index}
                        onDelete={canDelete ? () => deleteTask({ taskId: task.uuid }) : undefined}
                        onEdit={() => onEdit(task.uuid)}
                        copy={actionsCellCopy}
                    />
                )
            },
        }),
    ]

    return (
        <Table
            pagination={{
                pageSize: 15,
            }}
            columns={columns}
            data={tasks}
            columnPinning={{ right: ['actions'] }}
            controlledSizing={false}
            greyThemed
            width="100%"
            isFiltered={isFiltered}
            showNumberOfEntries
            copy={tableCopy}
        />
    )
}
